import { isObject, isFunction } from '@/utils/is';

/**
 * 获取时间段选项
 * @param {number} start 开始时间
 * @param {is_num} end 结束时间
 * @param {boolean} [is_num=false] 值是否为number类型
 * @returns {Object[]}
 */
export const getTimeOptions = (start, end, is_num = false) => {
  if (start > end) {
    return [];
  }
  const options = [];
  for (let i = start; i <= end; i++) {
    const hour = i < 10 ? `0${i}` : i;
    options.push({
      label: `${hour}:00`,
      value: is_num ? i : `${hour}:00:00`,
    });
  }
  return options;
};

/**
 * 获取年龄选项
 * @param {number} start 开始年龄
 * @param {number} end 结束年龄
 * @returns {Object[]}
 */
export const getAgeOptions = (start, end) => {
  if (start > end) {
    return [];
  }
  const options = [];
  for (let i = start; i <= end; i++) {
    options.push({
      label: `${i}岁`,
      value: i,
    });
  }
  return options;
};

/**
 * 获取日期 - yyyy-MM-dd格式
 * @param {number} day_num 天数
 * @returns {string} 日期字符串
 */
export const getDate = (day_num) => {
  const today = new Date();
  // 当前日期上新增天数
  day_num && today.setDate(today.getDate() + day_num);
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0'); // January is 0!
  const year = today.getFullYear();
  return `${year}-${month}-${day}`;
};

/**
 * 执行表单配置对象中的事件监听，事件支持change
 * @param {Object} form_config 表单配置对象：符合app-form组件配置的对象
 * @returns {void}
 */
export function executeFormFieldsEventListener(form_config) {
  try {
    Object.keys(form_config).forEach(key => {
      const form_item = form_config[key];
      // 检查表单项配置
      const valid = isObject(form_item) && Object.prototype.hasOwnProperty.call(form_item, 'on') && isFunction(form_item.on.change);
      if (valid) {
        try {
          form_item.on.change(form_item.value);
        } catch (error) {
          console.error(`Error executing change handler for key: ${key}`, error);
        }
      }
      // 检查对象自有属性children
      const child_valid = Object.prototype.hasOwnProperty.call(form_item, 'children') && isObject(form_item.children);
      if (child_valid) {
        executeFormFieldsEventListener(form_item.children);
      }
    });
  } catch (error) {
    console.error('Error executing change handlers', error);
  }
}

/**
 * 应用表单覆盖
 * @param {Object} config 配置
 * @param {Object} config.form_config 表单配置对象：符合app-form组件配置的对象
 * @param {Object} config.overrides 覆盖配置对象
 * @param {Object} [config.raw_config={}] 表单原始配置
 */
export function applyFormFieldsOverrides({ form_config, overrides, module_key, fields_key, raw_config = {}}) {
  function traverseConfig(previewed_config, full_path = '') {
    Object.keys(previewed_config).forEach(key => {
      const current_path = full_path ? `${full_path}.${key}` : key;
      const override = overrides[`${module_key}.${fields_key}.${current_path}`];
      override?.({
        key, // 字段名称
        field_config: previewed_config[key], // 字段配置
        raw_config, // 表单原始配置
      });
      if (isObject(previewed_config) && Object.prototype.hasOwnProperty.call(previewed_config[key], 'children')) {
        traverseConfig(previewed_config[key].children, current_path);
      }
    });
  }
  traverseConfig(form_config);
}

/**
 * 模块跑马灯
 * @param {Object} modules_config 模块配置
 * @param {Function} callback 回调函数
 */
export function modulesCarousel(modules_config, callback) {
  Object.keys(modules_config).forEach((module_key) => {
    const module_config = modules_config[module_key];
    Object.keys(module_config.config).forEach((fields_key) => {
      const form_config = module_config.config[fields_key];
      isFunction(callback) && callback({ module_key, fields_key, form_config });
    });
  });
}

/**
 * 生成字段名称
 * @param {string[]} keys 键名组成的数组
 * @param {string} separator 连接符
 * @returns {string}
 */
export const generateFieldName = (keys = [], separator = '.') => keys.join(separator);

/**
 * 深度合并模板
 * @param {Object[]} sources 对象组成的数组
 * @returns {Object}
 * @example
 * deepMerge([{ name: 1 }, { age: 2 } ]);
 * 输出：{ "name": 1, "age": 2 }
 *
 * deepMerge([{ name: 1, age: 1 }, { age: 2 } ]);
 * 输出：{ "name": 1, "age": 2 }
 *
 * deepMerge([{ name: 1, interest: ["football", "music"] }, { age: 2, interest: ["football"] } ]);
 * 输出：{ "name": 1, "interest": ["football", "music"], "age": 2 }
 *
 * deepMerge([{ name: 1, interest: [{ label: 1, value: 1 }, { label: 2, value: 2 }] }, { age: 2, interest: [{ label: 1, value: 1 }, { label: 3, value: 3 }] } ]);
 * 输出：{ "name": 1, "interest": [{ label: 1, value: 1 }, { label: 2, value: 2 }, { label: 3, value: 3 }], "age": 2 }
 */
export function deepMergeTemplate(sources) {
  function isArray(val) {
    return Object.prototype.toString.call(val) === '[object Array]';
  }

  if (!isArray(sources) || sources.length === 0) {
    return {};
  }

  const visited = new WeakMap();

  function isObject(val) {
    return Object.prototype.toString.call(val) === '[object Object]';
  }

  function isBasicType(val) {
    const type = typeof val;
    const types = ['number', 'string', 'boolean'];
    return types.includes(type);
  }

  /**
   * 子属性为基本类型的两个对象键值是否相等
   * @param {Object} obj1
   * @param {Object} obj2
   * @returns {boolean}
   */
  function isBasicObjEqual(obj1, obj2) {
    const is_options_type = [obj1, obj2].every(obj => {
      const keys = Object.keys(obj);
      return keys.includes('label') && keys.includes('value');
    });
    if (is_options_type) {
      return JSON.stringify({ label: obj1.label, value: obj1.value }) === JSON.stringify({ label: obj2.label, value: obj2.value });
    }
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  }

  function mergeArrays(targetArray, sourceArray) {
    const is_basic_object_arr = [targetArray, sourceArray].every(arr => {
      return arr.every(item => {
        return isObject(item) && Object.entries(item).every(([key, value]) => isBasicType(key) && isBasicType(value));
      });
    });
    if (is_basic_object_arr) {
      const result = [...targetArray];
      sourceArray.forEach(source_item => {
        if (!result.some(result_item => isBasicObjEqual(source_item, result_item))) {
          result.push(source_item);
        }
      });
      return result;
    }
    return [...new Set([...targetArray, ...sourceArray])];
  }

  function merge(target, source) {
    if (!isObject(target) || !isObject(source)) {
      return source;
    }

    for (const key in source) {
      if (source.hasOwnProperty(key)) {
        const source_value = source[key];
        let target_value = target[key];

        if (isObject(source_value) && !visited.has(source_value)) {
          visited.set(source_value, true);

          // 如果target中没有该key或对应的值不是对象，则初始化一个新对象进行合并
          if (!target.hasOwnProperty(key) || !isObject(target_value)) {
            target_value = {};
          }
          // 递归合并对象
          target[key] = merge(target_value, source_value);
        } else if (Array.isArray(source_value) && Array.isArray(target_value)) {
          // 合并数组 - key为value时直接赋值，避免出现默认值和模板值同时存在的情况
          const include_key = ['value', 'backup_value', 'options', 'backup_options'];
          target[key] = include_key.includes(key) ? source_value : mergeArrays(target_value, source_value);
        } else {
          // 直接赋值
          target[key] = source_value;
        }
      }
    }

    return target;
  }

  return sources.reduce((acc, curr) => merge(acc, curr), {});
}

/**
 * 根据文件路径提取渠道名称
 * @param {string} path 文件的相对路径
 * @param {string} part 匹配部分，例如文件相对路径是 ./GdtDataSourceController.js，则part参数传递DataSourceController
 * @returns {string|null}
 */
export function extractChannel(path, part) {
  const regex = new RegExp(`\\.\\/([^\\/]+)${part}\\.js$`);
  const match = path.match(regex);
  return match ? (match[1] || '').replace(/V3$/, '_V3').toLowerCase() : null;
}
